<template>
  <div class="login-container">
    <el-card class="login-card">
      <div class="login-header">
        <h2 class="login-title">大学生体测智能管理与运动健康分析系统</h2>
        <p class="login-subtitle">请选择角色并输入账号密码登录</p>
      </div>
      <el-form
        ref="loginFormRef"
        :model="loginForm"
        :rules="loginRules"
        label-width="80px"
        class="login-form"
      >
        <el-form-item label="用户名" prop="username">
          <el-input 
            v-model="loginForm.username" 
            placeholder="请输入用户名" 
            :prefix-icon="User" 
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            :prefix-icon="Lock"
            show-password
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select 
            v-model="loginForm.role" 
            placeholder="请选择角色" 
            :prefix-icon="Avatar"
          >
            <el-option label="学生" value="student"></el-option>
            <el-option label="教师" value="teacher"></el-option>
            <el-option label="管理员" value="admin"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item class="login-remember">
          <el-checkbox v-model="loginForm.remember">记住我</el-checkbox>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            class="login-btn"
            @click="handleLogin"
            :loading="loading"
          >
            登录
          </el-button>
        </el-form-item>
      </el-form>
      <!-- 系统提示信息 -->
      <div v-if="systemMessage" class="system-message" :class="messageType">
        {{ systemMessage }}
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { User, Lock, Avatar } from '@element-plus/icons-vue';
import { useAuthStore } from '@/store/modules/auth';
import request from '@/utils/request';

const router = useRouter();
const loading = ref(false);
const loginFormRef = ref(null);
const systemMessage = ref('');
const messageType = ref('info');

// 表单数据
const loginForm = reactive({
  username: '',
  password: '',
  role: '',
  remember: false
});

// 表单验证规则
const loginRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 30, message: '密码长度在 6 到 30 个字符', trigger: 'blur' }
  ],
  role: [
    { required: true, message: '请选择角色', trigger: 'change' }
  ]
};

const authStore = useAuthStore();

// 检查是否有记住的用户信息
const loadRememberedUser = () => {
  try {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedRole = localStorage.getItem('rememberedRole');
    const savedPassword = localStorage.getItem('rememberedPassword');
    
    if (savedUsername && savedRole) {
      loginForm.username = savedUsername;
      loginForm.role = savedRole;
      if (savedPassword) {
        loginForm.password = savedPassword;
        loginForm.remember = true;
      }
    }
  } catch (error) {
    console.error('加载记住的用户信息失败:', error);
  }
};

// 保存用户记住的信息
const saveRememberedUser = () => {
  try {
    if (loginForm.remember) {
      localStorage.setItem('rememberedUsername', loginForm.username);
      localStorage.setItem('rememberedRole', loginForm.role);
      localStorage.setItem('rememberedPassword', loginForm.password);
    } else {
      localStorage.removeItem('rememberedUsername');
      localStorage.removeItem('rememberedRole');
      localStorage.removeItem('rememberedPassword');
    }
  } catch (error) {
    console.error('保存记住的用户信息失败:', error);
  }
};

// 处理登录逻辑
const handleLogin = async () => {
  try {
    // 表单验证
    await loginFormRef.value.validate();
    loading.value = true;
    systemMessage.value = '';

    // 调用后端登录API，使用相对路径避免重复的API前缀
    const response = await request.post('/login', {
      username: loginForm.username,
      password: loginForm.password,
      role: loginForm.role
    });
    console.log('登录请求路径:', '/api/login');
    console.log('登录请求数据:', {
      username: loginForm.username,
      password: '******', // 密码脱敏
      role: loginForm.role
    });

    // 登录成功，保存用户信息
    // 根据request.js拦截器实现，response已经是response.data了
    if (response && response.code === 200 && response.data) {
      // 使用response.data作为userData
      const userData = response.data;
      
      // 保存用户信息到状态管理
      authStore.login(userData);
      
      // 保存到本地存储 - 只保存必要信息，完全基于token进行身份验证
      localStorage.setItem('token', userData.token);
      localStorage.setItem('username', userData.username || loginForm.username);
      localStorage.setItem('role', userData.role || loginForm.role);
      
      // 不再单独保存teacherId和studentId，完全依靠token进行身份识别
      // 移除以下代码，避免依赖特定ID进行身份验证
      /*
      // 根据角色保存特定ID
      if (userData.teacherId) {
        localStorage.setItem('teacherId', userData.teacherId.toString());
      }
      // 学生角色使用id作为studentId
      if (loginForm.role === 'student' && userData.id) {
        localStorage.setItem('studentId', userData.id.toString());
      }
      */
      
      // 创建并保存简化的userInfo对象，移除对teacherId和studentId的依赖
      const userInfo = {
        username: userData.username || loginForm.username,
        role: userData.role || loginForm.role,
        // 只保留用户基本信息，不保留ID信息，完全依靠token进行身份验证
        name: userData.name,
        college: userData.college,
        major: userData.major,
        className: userData.className
      };
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      // 保存记住我状态
      saveRememberedUser();
      
      // 显示成功消息
      ElMessage.success('登录成功，正在跳转...');
      
      // 根据角色跳转到对应首页
      setTimeout(() => {
        const roleRoutes = {
          student: '/',
          teacher: '/teacher/profile',
          admin: '/admin/activities'
        };
        router.push(roleRoutes[loginForm.role] || '/');
      }, 500);
    } else {
      throw new Error('登录失败，返回数据不完整');
    }
  } catch (error) {
    console.error('登录错误:', error);
    
    // 处理错误信息
    if (error.name === 'ValidateError') {
      // 表单验证错误，Element Plus会自动显示
      return;
    }
    
    // 显示具体错误信息
    const errorMessage = error.response?.data?.message || '登录失败，请检查用户名和密码是否正确';
    ElMessage.error(errorMessage);
    
    // 设置系统消息提示
    systemMessage.value = errorMessage;
    messageType.value = 'error';
    setTimeout(() => {
      systemMessage.value = '';
    }, 5000);
  } finally {
    loading.value = false;
  }
};

// 组件挂载时加载记住的用户信息
onMounted(() => {
  // 清理可能存在的不完整userInfo数据，确保重新登录能获取正确信息
  const storedUserInfo = localStorage.getItem('userInfo');
  try {
    // 不再检查teacherId，因为我们完全基于token进行身份验证
    // 只需确保userInfo是有效的JSON对象
    if (storedUserInfo) {
      const userInfo = JSON.parse(storedUserInfo);
      // 简单验证userInfo是否有效，不检查特定ID字段
      if (!userInfo || typeof userInfo !== 'object') {
        console.log('用户信息格式无效，清理存储数据');
        localStorage.removeItem('userInfo');
      }
    }
  } catch (error) {
    console.error('解析用户信息失败，清理存储数据:', error);
    localStorage.removeItem('userInfo');
  }
  
  loadRememberedUser();
});
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-image: url('@/assets/images/350.jpeg');
  background-size: cover;
  background-position: left center;
  background-repeat: no-repeat;
  background-color: #f5f7fa;
}

.login-card {
  width: 420px;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
  transition: all 0.3s ease;
}

.login-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.login-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.login-subtitle {
  font-size: 14px;
  color: #909399;
}

.login-form {
  padding: 0;
}

.login-remember {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.login-btn {
  width: 100%;
  height: 40px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 6px;
}

.system-message {
  margin-top: 15px;
  padding: 10px 15px;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
}

.system-message.info {
  background-color: #ecf5ff;
  color: #409eff;
  border: 1px solid #d9ecff;
}

.system-message.error {
  background-color: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fbc4c4;
}

/* 优化输入框样式 */
:deep(.el-input__wrapper) {
  border-radius: 6px;
  transition: all 0.3s ease;
}

:deep(.el-input__wrapper:hover) {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 120, 255, 0.1);
}

:deep(.el-input__wrapper.is-focus) {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 120, 255, 0.2);
}

/* 优化选择框样式 */
:deep(.el-select .el-input__wrapper) {
  border-radius: 6px;
}
</style>